import { useEffect, useMemo } from 'react';
import { Col } from 'reactstrap';
import {
  Alert,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import MultipleFileInput from '../../../../components/fileInput/MultipleFileInput';
import PetitionFileDownload from './PetitionFileDownload';
import { PetitionData, ViewPetition } from '../../../../types/petitions';
import {
  deleteTempFile,
  uploadTempFile,
} from '../../../../api/requests/petitions';

interface PetitionFormReintegroProps {
  programOptions: SelectOptionType[];
  /**
   * El estudiante no tiene ninguna matrícula elegible para reintegro (por
   * ejemplo, estado `expulsado`). Sin este aviso el formulario se abría con el
   * selector de programa vacío y sin explicación, dejando al estudiante sin
   * forma de completar la solicitud.
   */
  hasNoPrograms?: boolean;
  downloadFiles?: ViewPetition['docs'];
  downloadFileResponse?: ViewPetition['docResponse'];
  onlyView?: boolean;
  isResolved?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

export default function PetitionFormReintegro({
  programOptions = [],
  hasNoPrograms = false,
  onlyView = false,
  isResolved = false,
  downloadFiles,
  downloadFileResponse,
  onLoadingChange,
}: PetitionFormReintegroProps) {
  const { t } = useTranslation(TranslationsKeys.PETITIONS);
  const {
    validateTextNotEmpty,
    validatePhone,
    validateEmail,
  } = useValidations();
  const { control, setValue } = useFormContext<PetitionData>();

  const text = useMemo(() => {
    const prefix = 'modal.form';
    return {
      phone: t(`${prefix}.phone`),
      email: t(`${prefix}.email`),
      program: t(`${prefix}.reintegroProgram.label`),
      noPrograms: t(`${prefix}.reintegroProgram.noPrograms`),
      reason: {
        label: t(`${prefix}.reason.label`),
        placeholder: t(`${prefix}.reason.placeholder`),
      },
      file: {
        label: t(`${prefix}.file.label`),
      },
      download: {
        label: t(`${prefix}.download.label`),
        labelResponse: t(`${prefix}.download.labelResponse`),
        button: t(`${prefix}.download.button`),
      },
      validation: {
        select: t(`${prefix}.validation.select`),
        max: t(`${prefix}.validation.max`, { value: 500 }),
      },
    };
  }, [t]);

  const selectedProgram = useWatch({
    name: 'reintegroProgram',
    control,
  });

  useEffect(() => {
    if (selectedProgram?.label) {
      setValue('title', `Reintegro: ${selectedProgram.label}`.trim());
    }
  }, [selectedProgram, setValue]);

  if (hasNoPrograms && !onlyView) {
    return (
      <Col md={12} className="my-3">
        <Alert icon="information" text={text.noPrograms} />
      </Col>
    );
  }

  return (
    <>
      <Col md={6} className="my-3">
        <TextInput
          label={text.phone}
          name="cellPhone"
          disabled={onlyView}
          control={control}
          rules={{
            validate: { validatePhone },
          }}
        />
      </Col>
      <Col md={6} className="my-3">
        <TextInput
          label={text.email}
          name="email"
          control={control}
          disabled={onlyView}
          rules={{
            validate: { validateEmail, require: validateTextNotEmpty },
          }}
        />
      </Col>
      <Col md={12}>
        <Select
          label={text.program}
          control={control}
          disabled={onlyView}
          options={programOptions}
          rules={{ required: text.validation.select }}
          name="reintegroProgram"
          isClearable={false}
        />
      </Col>
      <Col md={12} className="my-3">
        <TextAreaInput
          label={text.reason.label}
          name="description"
          placeholder={text.reason.placeholder}
          disabled={onlyView}
          rules={{
            validate: {
              require: validateTextNotEmpty,
            },
            maxLength: { value: 500, message: text.validation.max },
          }}
          control={control}
        />
      </Col>
      <Col md={12} className="my-4">
        {onlyView ? (
          <PetitionFileDownload
            text={text.download.button}
            label={text.download.label}
            files={downloadFiles}
          />
        ) : (
          <MultipleFileInput
            name="docs"
            label={text.file.label}
            accept="image/png, image/jpeg, application/pdf"
            control={control}
            uploadTempFile={uploadTempFile}
            deleteTempFile={deleteTempFile}
            onLoadingChange={onLoadingChange}
          />
        )}
      </Col>
      {isResolved && downloadFileResponse && (
        <Col md={12} className="my-4">
          <PetitionFileDownload
            text={text.download.button}
            label={text.download.labelResponse}
            file={downloadFileResponse}
          />
        </Col>
      )}
    </>
  );
}
